import React from 'react';
import { theme } from '@noths/polaris-client-ribbons-base';
import { priceFormatter } from '@noths/polaris-client-utils';
import { CurrencySymbol, Price } from '@noths/polaris-dev-ts-types';

import { Accordion } from 'src/components/molecules/Accordion';
import { getFormattedPersonalisationAmount } from 'src/utils';
import {
  ITEM_PRICE_COPY,
  PERSONALISATION_ADDED_COPY,
  PERSONALISATION_COPY,
  PERSONALISATION_REMOVED_COPY,
  PRICE_BREAKDOWN_ADDED_TESTID,
  PRICE_BREAKDOWN_REMOVED_TESTID,
  TOTAL_COPY,
} from '../constants';
import { PRICE_BREAKDOWN_TESTID } from '../constants/testIds';
import * as styles from '../styles';

export interface PersonalisationBreakdown {
  amountAdded: number;
  amountRemoved: number;
}

export interface PriceBreakdownProps {
  productPrice: Price;
  totalPrice: number;
  personalisationsTotalAmount: number;
  breakdown: PersonalisationBreakdown;
  showTotalDivider?: boolean;
  expandablePersonalisations?: boolean;
}

const AddedAndRemovedAmounts = ({
  breakdown,
  productPrice,
  priceDigitLength,
}: Pick<PriceBreakdownProps, 'breakdown' | 'productPrice'> & {
  priceDigitLength: number;
}) => (
  <>
    {breakdown.amountAdded > 0 && (
      <>
        <p css={styles.priceLabel}>{PERSONALISATION_ADDED_COPY}</p>
        <p css={styles.priceAmount} data-testid={PRICE_BREAKDOWN_ADDED_TESTID}>
          <span css={styles.priceAmountPlusSymbol}>+</span>
          {priceFormatter(productPrice.currency, breakdown.amountAdded)}
        </p>
      </>
    )}
    {breakdown.amountRemoved < 0 && (
      <>
        <p css={styles.priceLabel}>{PERSONALISATION_REMOVED_COPY}</p>{' '}
        <p css={styles.priceAmount} data-testid={PRICE_BREAKDOWN_REMOVED_TESTID}>
          <span css={styles.priceAmountMinusSymbol} data-is-multi-digit={priceDigitLength > 1}>
            &minus;
          </span>
          {priceFormatter(productPrice.currency, breakdown.amountRemoved).replace('-', '')}
        </p>
      </>
    )}
  </>
);

export const PriceBreakdown = ({
  breakdown,
  personalisationsTotalAmount,
  productPrice,
  totalPrice,
  showTotalDivider,
  expandablePersonalisations,
}: PriceBreakdownProps) => {
  const { value: formattedPersonalisationsTotalAmount } = getFormattedPersonalisationAmount(
    productPrice.currency,
    personalisationsTotalAmount,
  );

  const priceDigitLength = formattedPersonalisationsTotalAmount
    .replace(CurrencySymbol[productPrice.currency], '')
    .replace('-', '')
    .split('.')[0].length;

  const shouldShowTotalDivider = showTotalDivider && personalisationsTotalAmount !== 0;

  return (
    <div data-testid={PRICE_BREAKDOWN_TESTID} css={styles.priceBreakdown}>
      {personalisationsTotalAmount !== 0 && (
        <>
          <p css={styles.priceLabel}>{ITEM_PRICE_COPY}</p>
          <p css={styles.priceAmount}>
            {priceFormatter(productPrice.currency, productPrice.amount)}
          </p>

          {expandablePersonalisations ? (
            <Accordion
              styles={styles.accordionStyles}
              title={
                <>
                  {PERSONALISATION_COPY}
                  <span css={styles.accordionTotalAmount}>
                    {personalisationsTotalAmount < 0 && <>&minus; </>}
                    {priceFormatter(productPrice.currency, personalisationsTotalAmount).replace(
                      '-',
                      '',
                    )}
                  </span>
                </>
              }
              id="personalisation-breakdown-accordion"
              extraContentHeight={theme.spacing.xl}
            >
              <div css={styles.expandablePersonalisationBreakdown}>
                <AddedAndRemovedAmounts
                  breakdown={breakdown}
                  productPrice={productPrice}
                  priceDigitLength={priceDigitLength}
                />
              </div>
            </Accordion>
          ) : (
            <div css={styles.personalisationBreakdown}>
              <p css={styles.personalisationBreakdownCopy}>{PERSONALISATION_COPY}</p>
              <AddedAndRemovedAmounts
                breakdown={breakdown}
                productPrice={productPrice}
                priceDigitLength={priceDigitLength}
              />
            </div>
          )}
        </>
      )}

      <p css={[styles.totalLabel, shouldShowTotalDivider && styles.totalDivider]}>{TOTAL_COPY}</p>
      <p css={[styles.totalPrice, shouldShowTotalDivider && styles.totalDivider]}>
        {priceFormatter(productPrice.currency, totalPrice)}
      </p>
    </div>
  );
};
